// src/control/botciergeController.js
// Sprint 3 + Sprint 4 + Sprint 4A
// Sprint 4A additions:
//   - botciergeSpeak: WS reconnect before synthesize (fixes Heroku 1006 mute bug)
//   - botciergeSpeak: closing brace fixed (was missing, causing parse error)


// ─────────────────────────────────────────────────────────────────────────────
// LOCAL STATE
// ─────────────────────────────────────────────────────────────────────────────
import { spokenText } from "../utils/spokenText";
let _setState    = null;
let _currentUser = null;
// Last known GPS fix, or null when we don't have one. Set once from the browser
// geolocation callback; read into every chat payload so FlowPlanner can reverse-
// geocode it. Stays null when permission is denied — see setUserLocation.
let _userLocation = null;

// Only accept a real coordinate pair. Anything else (denied permission, a stray
// {latitude:0,longitude:0}, a partial object) clears it, so the payload omits
// location entirely rather than sending zeros the backend would geocode to the
// Gulf of Guinea.
export function setUserLocation(loc) {
  const lat = Number(loc?.latitude);
  const lon = Number(loc?.longitude);
  _userLocation = (Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0))
    ? { latitude: lat, longitude: lon }
    : null;
}

export function registerController(setState) {
  _setState = setState;
}

export function controlBotcierge({ avatarType, emotion = "neutral", payload = null }) {
  if (!_setState) return;
  _setState({ avatarType, emotion, payload });
}

// ─────────────────────────────────────────────────────────────────────────────
// SPRINT 4 — AVATAR ENCAPSULATION
// ─────────────────────────────────────────────────────────────────────────────
export const AVATAR_OBJECTS = {
  Camille: {
    name:"Camille", glb:"/avatars/Camilia.glb?v=20261029", body:"F",
    voice:"af_bella", language:"en-us", speed:1, encoding:"wav",
    cameraY:-0.7, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"warm and professional", icon:"👩",
  },
  Prithi: {
    name:"Prithi", glb:"/avatars/prithi.glb?v=20261029", body:"F",
    // C4: younger American female — was ef_dora (client: "sounds like an old woman").
    // Sprint 4C: Prithi previously shared af_bella with Camille (client: "Prithi now has
    // Camille's voice"). Now owns af_nova — a distinct feminine voice added to public/voices.
    voice:"af_nova", language:"en-us", speed:1, encoding:"wav",
    cameraY:0.1, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"precise and professional", icon:"🧕",
  },
  Benjamin: {
    name:"Benjamin", glb:"/avatars/Benji.glb?v=20261029", body:"M",
    voice:"am_fenrir", language:"en-us", speed:1, encoding:"wav",
    cameraY:0.1, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"confident and direct", icon:"🧔",
  },
  John: {
    name:"John", glb:"/avatars/john.glb?v=20261029", body:"M",
    voice:"am_fenrir", language:"en-us", speed:1, encoding:"wav",
    cameraY:0.1, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"calm and authoritative", icon:"👨",
  },
  Margie: {
    name:"Margie", glb:"/avatars/margie.glb?v=20261029", body:"F",
    // C3: distinct from Camille (af_bella) and from Prithi's prior ef_dora — af_sky.bin
    voice:"af_sky", language:"en-us", speed:1, encoding:"wav",
    cameraY:0.1, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"friendly and warm", icon:"👩‍🦰",
  },
  // Genie 3D (10 Oct 2026): GENIE.glb with its 2048 textures re-encoded as JPEG
  // (8.8 MB, was 14.1 MB; PSNR 42-44 dB), same rig and 72 morph targets
  // (15 visemes) as Margie. A half-body model: the leg bones TalkingHead needs
  // were added from Margie's skeleton (not skinned, nothing visible changes).
  Genie: {
    name:"Genie", glb:"/avatars/genie.glb?v=20261029", body:"M",
    // A deeper voice than the other male avatars (client, 10 Oct): Kokoro am_onyx,
    // 2D uses the same voice (src/utils/avatarVoice.js, 26 Oct).
    voice:"am_onyx", language:"en-us", speed:1, encoding:"wav",
    cameraY:0.1, cameraX:0.0, cameraFOV:10,
    cameraRotateX:-0.1, cameraRotateY:0.0, cameraRotateZ:2,
    personality:"warm and wise", icon:"🧞",
  },
};

export function getCurrentAvatarObject() {
  return window.currentAvatarObject || AVATAR_OBJECTS.Camille;
}

export function setCurrentAvatarObject(avatarName) {
  window.currentAvatarObject = AVATAR_OBJECTS[avatarName] || AVATAR_OBJECTS.Camille;
  console.log(`[AvatarObject] Active: ${window.currentAvatarObject.name} | Voice: ${window.currentAvatarObject.voice}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// WEB SPEECH FALLBACK
// ─────────────────────────────────────────────────────────────────────────────
function _speakWebSpeech(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const avatarObj = window.currentAvatarObject;
  const isMale = avatarObj?.name === "Benjamin" || avatarObj?.name === "John";

  const doSpeak = () => {
    const utter = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const femaleNames = /samantha|victoria|karen|zira|moira|fiona|tessa|susan|female|woman/i;
    const maleNames   = /david|james|daniel|alex|guy|fred|mark|thomas|male|man/i;
    let voice;
    if (isMale) {
      voice = voices.find(v => v.lang.startsWith("en") && maleNames.test(v.name))
           || voices.find(v => v.lang.startsWith("en") && !femaleNames.test(v.name));
    } else {
      voice = voices.find(v => v.lang.startsWith("en") && femaleNames.test(v.name))
           || voices.find(v => v.lang.startsWith("en") && !maleNames.test(v.name));
    }
    if (voice) utter.voice = voice;
    utter.pitch = isMale ? 0.82 : 1.1;
    utter.rate  = 0.95;
    utter.onstart = () => { window._ttsPlaying = true; };
    utter.onend   = () => { window._ttsPlaying = false; };
    utter.onerror = () => { window._ttsPlaying = false; };
    window._ttsPlaying = true;
    window.speechSynthesis.speak(utter);
  };

  const voices = window.speechSynthesis.getVoices();
  if (voices.length > 0) {
    doSpeak();
  } else {
    window.speechSynthesis.onvoiceschanged = () => {
      window.speechSynthesis.onvoiceschanged = null;
      doSpeak();
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SPEAK
// Sprint 4A fix: reconnect WebSocket before synthesizing if Heroku dropped it
// Bug was: WS closes with code 1006 (Heroku 55s idle timeout) while GLB loads.
// synthesize() was called on a dead socket → silent avatar.
// Fix: check ws.readyState before calling synthesize(). If not OPEN, reconnect first.
// ─────────────────────────────────────────────────────────────────────────────
export function botciergeSpeak(text, emotion = "neutral", avatarType = null, onWord = null) {
  if (emotion && _setState) {
    _setState(prev => ({ ...prev, emotion }));
  }

  const explicitType = avatarType === "2D" || avatarType === "3D" ? avatarType : null;
  const type = explicitType || (window.currentAvatarHead ? "3D" : "2D");

  // Word-by-word reveal timed to TTS synthesis + playback
  if (onWord && text) {
    const words = text.split(" ");
    const SYNTHESIS_DELAY = 1800;
    const wordDelay = (word) => {
      const base  = 100;
      const extra = Math.max(0, (word.length - 4) * 15);
      const punct = /[.,!?;:]/.test(word) ? 120 : 0;
      return base + extra + punct;
    };
    let delay = SYNTHESIS_DELAY;
    words.forEach((word, i) => {
      setTimeout(() => {
        if (window._wordRevealAborted) return;
        onWord(words.slice(0, i + 1).join(" "));
      }, delay);
      delay += wordDelay(word);
    });
  }

  // Client QA (10 Oct): the spoken copy only — never URLs, emails, markdown,
  // code, slashes or hyphen runs; a long reply is its first sentence or two and
  // "The full answer is on your screen." The on-screen text is unchanged.
  const said = spokenText(text);
  if (!said) return;

  if (type === "2D") {
    if (window.camille2DStop)  window.camille2DStop();
    if (window.camille2DSpeak) {
      window.camille2DSpeak(said);
    } else {
      window.speechSynthesis?.cancel();
      const utter = new window.SpeechSynthesisUtterance(said);
      utter.onstart = () => { window._ttsPlaying = true; };
      utter.onend   = () => { window._ttsPlaying = false; };
      utter.onerror = () => { window._ttsPlaying = false; };
      window._ttsPlaying = true;
      window.speechSynthesis?.speak(utter);
    }
  } else {
    if (window.camilleResumeAudio) window.camilleResumeAudio();

    if (window.currentAvatarTTS) {
      // Clear queued audio and reset gain
      try {
        if (window.currentAvatarHead?.ttsQueue) window.currentAvatarHead.ttsQueue = [];
        const gm  = window.currentAvatarHead?.gainMaster;
        const ctx = window.currentAvatarHead?.audioCtx;
        if (gm && ctx?.state === "running") {
          gm.gain.cancelScheduledValues(ctx.currentTime);
          gm.gain.setValueAtTime(0, ctx.currentTime);
          gm.gain.setValueAtTime(1, ctx.currentTime + 0.05);
        }
      } catch(_) {}

      const tts       = window.currentAvatarTTS;
      const avatarObj = window.currentAvatarObject;

      const doSynthesize = () => {
        try {
          tts.synthesize({ input: said });
          console.log("[TTS] ✅ HeadTTS synthesize called");
        } catch(e) {
          console.warn("[TTS] HeadTTS synthesize failed:", e.message);
          _speakWebSpeech(said);
        }
      };

      // Sprint 4A FIX: tts.ws is undefined on deployed HeadTTS — ALWAYS reconnect before synthesize.
      // Sprint 4B: retry up to 3 times with 2s delay before Web Speech fallback.
      let attempts = 0;
      const maxAttempts = 3;
      const trySpeak = () => {
        if (attempts >= maxAttempts) {
          console.warn("[TTS] All reconnect attempts failed — falling back to Web Speech");
          _speakWebSpeech(said);
          return;
        }
        attempts++;
        console.log(`[TTS] Connect attempt ${attempts}/${maxAttempts}`);
        tts.connect(null, () => {})
          .then(() => {
            console.log("[TTS] ✅ WS ready — synthesizing");
            if (avatarObj) {
              try {
                tts.setup({
                  voice:         avatarObj.voice,
                  language:      avatarObj.language || "en-us",
                  speed:         avatarObj.speed    || 1,
                  audioEncoding: avatarObj.encoding || "wav",
                });
              } catch(_) {}
            }
            doSynthesize();
          })
          .catch(() => {
            setTimeout(trySpeak, 2000);
          });
      };
      trySpeak();
    } else {
      // No HeadTTS at all — use Web Speech API
      console.log("[TTS] No HeadTTS — using Web Speech API");
      _speakWebSpeech(said);
    }
  }
} // ← FIX: this closing brace was MISSING in the original — caused a parse error

export function speak(text) { botciergeSpeak(text); }

// ─────────────────────────────────────────────────────────────────────────────
// SPRINT 4 — SEARCH ENGINE
// ─────────────────────────────────────────────────────────────────────────────
export const SEARCH_PATTERNS = [
  /^search\s+for\s+information\s+on\s+(.+)/i,
  /^search\s+for\s+(.+)/i,
  /^search\s+(.+)/i,
];

export function detectSearchIntent(text) {
  const trimmed = text.trim();
  for (const pattern of SEARCH_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) return { isSearch: true, query: match[1].trim() };
  }
  return { isSearch: false, query: null };
}

export function buildSearchGuidanceResponse(query) {
  const responses = [
    `I searched for "${query}". As a search engine like Google, I can help you find information — try being more specific about what you need!`,
    `Great search! "${query}" — BOTCIERGE is your personal AI search engine. For specialized tasks, check out our BOT Store for services that can help you do more.`,
    `You seem to be looking for information on "${query}". I can help you search — or if you need a specific service, say "what can you do" to see what's available.`,
  ];
  return responses[Math.floor(Math.random() * responses.length)];
}

export function buildSearchRedirectResponse(name = '') {
  const responses = [
    `You seem to be unclear about what to enter${name ? ", " + name : ""}. We are a search engine just like Google — go ahead and enter your search criteria and I can help you.`,
    `BOTCIERGE is the OS for Agentic AI. Feel free to browse our online BOT Store to find the BOT Services you can use to expand my skills. Get a subscription and try our services free for a limited time!`,
    `Try starting with "Search for..." followed by what you're looking for. I'm here to help you find information and connect you with the right services!`,
  ];
  return responses[Math.floor(Math.random() * responses.length)];
}

// ─────────────────────────────────────────────────────────────────────────────
// INTENT DETECTION
// ─────────────────────────────────────────────────────────────────────────────
// Profanity is screened by the backend's one prefilter (MBTS src/intent/prefilter.js).
// The canned FAQ lines are retired: questions about BOTCIERGE are answered by
// the LLM on the back end (BOTCIERGE_QUESTIONS_TO_LLM_ENABLED).

export function invalidIntentMessage(name = '') {
  return buildSearchRedirectResponse(name);
}

// NOTE (per BizBarrett): task/to-do phrases must NOT be intercepted here.
// TASKMASTER AI lives in his mbts backend (persisted todo list + emergency
// logic that feeds FlowPlanner), so those messages flow through untouched.
const INTENT_MAP = {
  LOGIN:  /\b(i\s+want\s+to\s+log\s*in|log\s*in|sign\s*in|my\s+account|account)\b/i,
  LOGOUT: /\b(log\s*out|sign\s*out|goodbye|bye)\b/i,
};

export function detectIntent(text) {
  for (const [intent, regex] of Object.entries(INTENT_MAP)) {
    if (regex.test(text)) return intent;
  }
  return "CHAT";
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN
// ─────────────────────────────────────────────────────────────────────────────
export const LOGIN_PROMPT    = "What's your first name?";

// Called once the mbts gate has verified who the user is; it hands back the _id
// and this fills in the profile the rest of the app reads.
export async function loadProfile(userId) {
  const res = await fetch("/api/auth/profile", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Account not found");
  }
  const data   = await res.json();
  _currentUser = { ...data.user, token: data.token };
  return _currentUser;
}

export function getUser()    { return _currentUser; }
export function setUser(u)   { _currentUser = u; }
// SESSIONS_ENABLED: signing out also ends the MBTS session (the proxy clears
// its cookies); everywhere = every device of this account.
export function logoutUser({ everywhere = false } = {}) {
  _currentUser = null;
  try {
    fetch(`/api/auth/${everywhere ? "logout-all" : "logout"}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }).catch(() => {});
  } catch (_) {}
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY LEDGER
// ─────────────────────────────────────────────────────────────────────────────
export async function checkActivityLedger(userId, token) {
  if (!userId) return null;
  try {
    const res = await fetch("/api/activityLedger", {
      method: "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data || null;
  } catch (err) {
    console.warn("[ActivityLedger] Could not reach endpoint:", err.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT
// ─────────────────────────────────────────────────────────────────────────────
// The device's own date (YYYY-MM-DD). The back end runs in UTC; this is the
// person's "today" for schedules and flow plans.
export function localDateString(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export async function sendToBackend({
  message, avatarName, user, sessionName = "",
  srxType = null, packages = [], srxState = null, conversationHistory = [],
  imageData = null,
  askFiles = [],
  secqForm = null,
}) {
  const headers = { "Content-Type": "application/json" };
  if (user?.token) headers["Authorization"] = `Bearer ${user.token}`;

  const controller = new AbortController();
  // ASK answers that search the web or run code take several seconds; stay just
  // under the 30s Heroku router limit instead of giving up at 8s.
  const timeout    = setTimeout(() => controller.abort(), 28000);

  try {
    const res = await fetch("/api/chat", {
      method: "POST",
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        message,
        avatar:        avatarName,
        userId:        user?._id    || null,
        userName:      user?.name   || sessionName || null,
        sessionName,
        authenticated: !!user,
        srxType,
        packages:      packages || user?.package || [],
        srxState,
        conversationHistory,
        imageData,
        localDate:     localDateString(),
        // "change my security questions": the form (2 registry ids + 2 answers).
        ...(secqForm ? { secqForm } : {}),
        // ASK attachments (Round 10): ids of files uploaded for this request.
        ...(Array.isArray(askFiles) && askFiles.length ? { askFiles } : {}),
        // Present only when we hold a real fix. Omitted entirely otherwise, so
        // the backend falls back to the person's home location on its own.
        ...( _userLocation ? { location: _userLocation } : {} ),
      }),
    });
    clearTimeout(timeout);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Chat API unavailable");
    }
    return await res.json();
  } catch (err) {
    clearTimeout(timeout);
    throw err;
  }
}
